import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as z from "zod";
import { WorkspaceTypeOptions, emailContainsPlus } from "@/lib/utils";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { useNavigate } from "react-router";
import { sandboxApi } from "@/api/auth-api";
import { useMutation } from "@tanstack/react-query";
const createSandboxSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .email("Invalid email address")
    .refine((val) => !emailContainsPlus(val), {
      message: "Email addresses containing '+' are not allowed",
    }),
  companyName: z.string().min(1, "Company name is required"),
  workspaceName: z.string().min(1, "Workspace name is required"),
  organizationType: z.enum(WorkspaceTypeOptions),
  referralSource: z.string().min(1, "Please select where you heard about us"),
  otherReferralSource: z.string().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
});

type CreateSandboxFormValues = z.infer<typeof createSandboxSchema>;

export function CreateSandboxForm() {
  const navigate = useNavigate();
  const [showOtherField, setShowOtherField] = useState(false);

  const form = useForm<CreateSandboxFormValues>({
    resolver: zodResolver(createSandboxSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      workspaceName: "",
      organizationType: undefined,
      referralSource: "",
      otherReferralSource: "",
    },
  });

  const createSandboxMutation = useMutation({
    mutationFn: (values: CreateSandboxFormValues) =>
      sandboxApi.createCompanySandbox({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        referralSource: values.referralSource,
        otherReferralSource: values.otherReferralSource,
        companyName: values.companyName,
        workspaceName: values.workspaceName,
        workspaceType: values.organizationType,
      }),
    onSuccess: (response) => {
      if (response.status === "success") {
        toast.success("Account created successfully! Please login.");
        // Redirect to login page with the email as a query parameter
        navigate(
          `/sign-in?email=${encodeURIComponent(
            response.result?.user?.email || ""
          )}`
        );
      } else {
        toast.error(
          getErrorMessage(
            response,
            response.message || "Failed to create account"
          )
        );
      }
    },
    onError: (error) => {
      console.error("Failed to create account:", error);
      toast.error(
        getErrorMessage(error, "Failed to create account. Please try again.")
      );
    },
  });

  const onSubmit = (values: CreateSandboxFormValues) => {
    createSandboxMutation.mutate(values);
  };

  return (
    <div className="w-full space-y-6">
      <div className="space-y-1.5">
        <h1 className="text-[1.75rem] font-bold tracking-tight text-foreground">
          Get Started
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter your details to get started
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your first name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter your email"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="workspaceName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Workspace Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your workspace name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organizationType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organization type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {WorkspaceTypeOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="referralSource"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Where did you hear about us?</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    setShowOtherField(value === "Other");
                  }}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select referral source" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Networking/Conference">
                      Networking/Conference
                    </SelectItem>
                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                    <SelectItem value="Instagram">Instagram</SelectItem>
                    <SelectItem value="Twitter/X">Twitter/X</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="Introduction">Introduction</SelectItem>
                    <SelectItem value="Search Engine">Search Engine</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {showOtherField && (
            <FormField
              control={form.control}
              name="otherReferralSource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Please specify</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter where you heard about us"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="acceptTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I accept the{" "}
                    <a
                      href="https://www.dgpt.io/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      terms and conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://www.dgpt.io/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      privacy policy
                    </a>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-11 font-medium"
            disabled={createSandboxMutation.isPending}
          >
            {createSandboxMutation.isPending
              ? "Creating account..."
              : "Get Started"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
